import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';
import { DEFAULT_VOICES } from '../config/voices';

export const useVoicePreferences = (userId) => {
  const [voicePreferences, setVoicePreferences] = useState(DEFAULT_VOICES);
  const [loading, setLoading] = useState(true);

  const loadVoicePreferences = useCallback(async () => {
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('voice_preferences')
        .eq('id', userId)
        .single();

      if (profile?.voice_preferences) {
        // Validate voice preferences to ensure gender-appropriate voices
        const femaleVoices = ['nova', 'shimmer', 'alloy'];
        const maleVoices = ['onyx', 'echo', 'fable'];
        const validatedPreferences = { ...DEFAULT_VOICES };
        
        // Validate female voice
        if (profile.voice_preferences.female && femaleVoices.includes(profile.voice_preferences.female)) {
          validatedPreferences.female = profile.voice_preferences.female;
        } else if (profile.voice_preferences.female) {
          console.warn(`useVoicePreferences: Invalid female voice "${profile.voice_preferences.female}", using nova`);
        }
        
        // Validate male voice
        if (profile.voice_preferences.male && maleVoices.includes(profile.voice_preferences.male)) {
          validatedPreferences.male = profile.voice_preferences.male;
        } else if (profile.voice_preferences.male) {
          console.warn(`useVoicePreferences: Invalid male voice "${profile.voice_preferences.male}", using onyx`);
        }
        
        console.log('useVoicePreferences: Validated preferences:', validatedPreferences);
        setVoicePreferences(validatedPreferences);
      } else {
        console.log('useVoicePreferences: No preferences found, using defaults');
        setVoicePreferences(DEFAULT_VOICES);
      }
    } catch (error) {
      console.error('Error loading voice preferences:', error);
      setVoicePreferences(DEFAULT_VOICES);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadVoicePreferences();
    }
  }, [userId, loadVoicePreferences]);

  const updateVoicePreferences = async (newPreferences) => {
    try {
      // Validate preferences before saving
      const femaleVoices = ['nova', 'shimmer', 'alloy'];
      const maleVoices = ['onyx', 'echo', 'fable'];
      const validatedPreferences = { ...DEFAULT_VOICES };
      
      // Validate female voice
      if (newPreferences.female && femaleVoices.includes(newPreferences.female)) {
        validatedPreferences.female = newPreferences.female;
      } else if (newPreferences.female) {
        console.warn(`updateVoicePreferences: Invalid female voice "${newPreferences.female}", using nova`);
        validatedPreferences.female = 'nova';
      }
      
      // Validate male voice
      if (newPreferences.male && maleVoices.includes(newPreferences.male)) {
        validatedPreferences.male = newPreferences.male;
      } else if (newPreferences.male) {
        console.warn(`updateVoicePreferences: Invalid male voice "${newPreferences.male}", using onyx`);
        validatedPreferences.male = 'onyx';
      }
      
      console.log('updateVoicePreferences: Saving validated preferences:', validatedPreferences);
      
      const { error } = await supabase
        .from('user_profiles')
        .update({ voice_preferences: validatedPreferences })
        .eq('id', userId);

      if (error) throw error;

      setVoicePreferences(validatedPreferences);
      return { success: true };
    } catch (error) {
      console.error('Error updating voice preferences:', error);
      return { success: false, error: error.message };
    }
  };

  return {
    voicePreferences,
    loading,
    updateVoicePreferences,
    refreshPreferences: loadVoicePreferences
  };
};
